import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { ApiClient } from '../api/client.js';
import { ApiError, isAbortError, NetworkError } from '../api/error.js';
import type { FloorPlan, Hotspot, ModelMeta, ModelMetaVariant, Preset } from '../api/types.js';
import {
  cameraHeadingDeg,
  isCalibrationDegenerate,
  pixelToWorld,
  worldToPixel,
} from '../floorplan/calibration.js';
import { floorplanSnapshot, floorplanTransition } from '../floorplan/state-machine.js';
import type {
  FloorplanEvent,
  FloorplanPosition,
  FloorplanSnapshot,
  FloorplanState,
} from '../floorplan/types.js';
import { projectWorldToScreen } from '../hotspots/projection.js';
import { hotspotsSnapshot, hotspotsTransition, sortHotspots } from '../hotspots/state-machine.js';
import type {
  HotspotScreenPosition,
  HotspotsEvent,
  HotspotsSnapshot,
  HotspotsState,
} from '../hotspots/types.js';
import type { DeviceCapabilities, DeviceMatcher } from '../lib/device.js';
import { detectDeviceClass, readDeviceClassInputs } from '../lib/device.js';
import { createFpsMonitor } from '../lib/fps-monitor.js';
import { exitFullscreen, isFullscreen, requestFullscreen } from '../lib/fullscreen.js';
import { bindShortcuts } from '../lib/keyboard.js';
import { defaultScreenshotFilename, downloadBlob } from '../lib/screenshot.js';
import { SHORTCUTS } from '../lib/shortcuts.js';
import { type BlobUrlHandle, loadModelAsBlobUrl } from '../loader/model-loader.js';
import { selectVariantForDevice } from '../loader/variant-resolver.js';
import { measurementsSnapshot, measurementsTransition } from '../measure/state-machine.js';
import type {
  MeasurementProjection,
  MeasurementsEvent,
  MeasurementsSnapshot,
  MeasurementsState,
  MeasurementUnits,
} from '../measure/types.js';
import { copyText } from '../share/clipboard.js';
import { decodeCameraState, encodeCameraState } from '../share/codec.js';
import { buildShareUrl } from '../share/url-builder.js';
import type { AppStore } from '../state/store.js';
import type { AppState, ParsedRoute, SceneMetadata } from '../state/types.js';
import { type CameraTarget, flyToCamera } from '../tour/animator.js';
import { sortPresets, tourSnapshot, tourTransition } from '../tour/state-machine.js';
import { type TourEvent, type TourSnapshot, type TourState, TRANSITION_MS } from '../tour/types.js';
import { parseShareUrl } from '../url/parser.js';
import {
  canCaptureFrame,
  canDispose,
  canEnterXR,
  canExitXR,
  canFitCamera,
  canRaycast,
  canSetBeforeRender,
  canSubscribeFrames,
  type ViewerAdapter,
} from '../viewer/adapter.js';
import { createOrbitControls } from '../viewer/orbit-controls.js';
import type { InitOptions } from '../viewer/types.js';
import { applyGravity, clampPitch, computeVelocity, forwardVector } from '../walk/movement.js';
import { walkSnapshot, walkTransition } from '../walk/state-machine.js';
import {
  EYE_HEIGHT,
  FLOOR_HEIGHT,
  JUMP_VELOCITY,
  MOUSE_SENSITIVITY,
  ON_FLOOR_EPSILON,
  WALK_SPEED,
  type WalkEvent,
  type WalkSnapshot,
  type WalkState,
} from '../walk/types.js';
import { detectXRSupport } from '../xr/detection.js';
import { xrSnapshot, xrTransition } from '../xr/state-machine.js';
import {
  INITIAL_XR_STATE,
  type XREvent,
  type XRMode,
  type XRSnapshot,
  type XRState,
} from '../xr/types.js';
import { fetchMetadataTolerant } from './parallel-metadata.js';

export interface OrchestratorDeps {
  apiClient: ApiClient;
  adapter: ViewerAdapter;
  store: AppStore;
  canvas: HTMLCanvasElement;
  href: string;
  embed: boolean; // controls fallback init opts
  // 4.M: pointer / hover / reduced-motion live capability matcher.
  // Owned by bootstrap; orchestrator subscribes + re-broadcasts via
  // 'device-state' event so the renderer stays event-driven (pattern
  // affermato `orchestrator emits → render subscribes`).
  deviceMatcher: DeviceMatcher;
  // Step 7 Phase 0 — optional async hook called AFTER fetchMeta success
  // and BEFORE dispatch META_FETCHED. Returning false dispatches
  // LOAD_CANCELLED_BY_USER instead. Used by main.ts bootstrap to show
  // mobile OOM guardrail modal when phone+ply+size>150MB. The hook
  // receives signal so it can react to dispose/abort while modal is
  // open (e.g. user navigates away).
  shouldProceedAfterMeta?: (meta: ModelMeta, ctx: { signal: AbortSignal }) => Promise<boolean>;
}

// 4.M walk-error reason discriminator. The toast component picks
// one of 4 contextual messages based on (reason, embed) tuple.
export type WalkErrorReason = 'pointer-lock-failed' | 'touch' | 'unsupported';

export interface OrchestratorEventDetailMap {
  'perf-toggle': { visible: boolean };
  'fps-update': { fps: number };
  'fps-warning': { fps: number };
  'fullscreen-error': { message: string };
  'share-result': { success: boolean; message?: string };
  // 4.F: tour state-machine snapshot. Emitted on every real state transition
  // and throttled to ~10 hz on TICK no-op for dwell countdown UI.
  'tour-state': TourSnapshot;
  // 4.F: emitted only when applyPreset runs the manual idle path; tour-driven
  // preset changes are reflected via 'tour-state' events instead.
  'preset-applied': { presetId: string };
  // 4.G: hotspot UI state snapshot — emitted on real state transitions
  // (open/close/next/prev/toggle visibility).
  'hotspot-state': HotspotsSnapshot;
  // 4.G: per-frame projected screen positions for each hotspot. Emitted
  // only when loaded + visible + total > 0 (see attachLoadedFeatures
  // ticker); UI components apply transform: translate(x, y) without
  // touching Three.js.
  'hotspot-positions': { positions: readonly HotspotScreenPosition[] };
  // 4.H: floor plan minimap state snapshot.
  'floorplan-state': FloorplanSnapshot;
  // 4.H: per-frame projected camera position (pixel space) + heading.
  // Emitted only when floorplan is present, visible, and calibration is
  // not degenerate (cleanup contract).
  'floorplan-position': FloorplanPosition;
  // 5.D: floor plan replaced (cardinality 0<->1 OR new image_url).
  // Renderer cleanup+remount minimap on this event so the dot/cone
  // start fresh and don't show stale calibration for one frame.
  'floorplan-replaced': { hadFloorplan: boolean; hasFloorplan: boolean };
  // 4.J: measurements state snapshot (active toggle + points + units).
  'measurement-state': MeasurementsSnapshot;
  // 4.J: per-frame projected screen positions for each measurement
  // point + cumulative segment distances (meters). Emitted only when
  // measureState.active && points.length > 0 (cleanup contract).
  'measurement-projections': {
    projections: readonly MeasurementProjection[];
    distances: readonly number[];
  };
  // 4.I1: walk mode (FPV) state snapshot. Emitted on toggle + on
  // automatic exits (pointer-lock unsupported / browser ESC / etc.).
  'walk-state': WalkSnapshot;
  // 4.K: WebXR (VR / AR) modal + session state snapshot. Emitted on
  // every real state transition (open/close modal, detection result,
  // session start/end, error). dispatchXREvent guards stale snapshot
  // emission after nested rollback (same pattern as walk-state).
  'xr-state': XRSnapshot;
  // 4.M: device capability snapshot (pointer modality, hover, reduced
  // motion). Emitted on every matchMedia change so toolbar / hints /
  // walk gating reactively update without remounting Loaded.
  'device-state': DeviceCapabilities;
  // 4.M: walk failure context (pointer-lock unsupported / iframe
  // policy denial / touch device). Emitted alongside the existing
  // 'walk-state' EXIT so the UI can show a toast with explanation.
  'walk-error': { reason: WalkErrorReason; embed: boolean };
  // 5.C: emitted after the orchestrator has finished wiring controls
  // and loaded features (post attachOrbitControls + attachLoadedFeatures).
  // Studio Web embedded bridge resolves its `ready` Promise on this
  // event — NOT on the store's `kind === 'loaded'` (which fires before
  // controls/features are available, racing capture-camera/raycast).
  'viewer-ready': Record<string, never>;
  // 5.C: emitted on a fatal viewer boot failure (load error after
  // exhaust, adapter init exception). Studio embedded bridge maps to
  // VIEWER_FAILURE editor SM event.
  'viewer-failure': { message: string };
  // 5.C iter 6 fix: emitted by replaceHotspots/replacePresets when the
  // sorted list cardinality crosses 0↔N. Triggers a renderer remount of
  // the Loaded component so newly-created hotspot/preset overlays appear
  // without a page refresh (mountHotspotDots etc. are no-op when wired
  // with an empty list).
  'metadata-replaced': { kind: 'hotspots' | 'presets' };
}

export interface Orchestrator {
  readonly events: EventTarget;
  start(): Promise<void>;
  dispose(): Promise<void>;
  takeScreenshot(): Promise<void>;
  resetView(): void;
  toggleFullscreen(): Promise<void>;
  togglePerfHud(): void;
  shareUrl(): Promise<void>;
  // 4.F: presets + tour. sortedPresets() returns the canonical session order
  // used by the SM and the pill UI; getTourSnapshot() seeds the renderer's
  // initial Loaded state without waiting for the first event.
  applyPreset(presetId: string): Promise<void>;
  startTour(): void;
  pauseTour(): void;
  resumeTour(): void;
  exitTour(): void;
  nextTourStep(): void;
  prevTourStep(): void;
  getSortedPresets(): readonly Preset[];
  getTourSnapshot(): TourSnapshot;
  // 4.G: hotspots presence + interaction. Same provider pattern as 4.F so
  // the renderer can seed Loaded mount without waiting for the first event.
  openHotspot(id: string): void;
  closeHotspot(): void;
  nextHotspot(): void;
  prevHotspot(): void;
  toggleHotspots(): void;
  getSortedHotspots(): readonly Hotspot[];
  getHotspotsSnapshot(): HotspotsSnapshot;
  // 4.H: floor plan minimap controls + getters.
  toggleFloorplan(): void;
  closeFloorplan(): void;
  reportFloorplanImageError(): void;
  // teleportOnFloorplan accepts world XZ. teleportOnFloorplanPixel is a
  // convenience that runs pixelToWorld(cal) + teleport — UI components
  // emit svg pixel coords and wire to this directly without importing
  // the calibration helper.
  teleportOnFloorplan(worldXZ: readonly [number, number]): void;
  teleportOnFloorplanPixel(pixelXY: readonly [number, number]): void;
  getFloorplan(): FloorPlan | null;
  getFloorplanSnapshot(): FloorplanSnapshot;
  // 5.D: author replaces (or removes) the floor plan at runtime. Updates
  // the cached FloorPlan + recomputes degenerate calibration flag, emits
  // 'floorplan-state' (existing) + 'floorplan-replaced' (new). Caller
  // is responsible for ordering — typically called after the studio
  // author flow completes the upload + the GET refetch returns.
  replaceFloorplan(fp: FloorPlan | null): void;
  // 4.J: measurement controls + getters. addMeasurePoint runs raycast
  // internally (early-return on no-hit / degenerate state).
  toggleMeasure(): void;
  exitMeasure(): void;
  addMeasurePoint(screenX: number, screenY: number): void;
  removeLastMeasurePoint(): void;
  resetMeasurements(): void;
  setMeasurementUnits(units: MeasurementUnits): void;
  getMeasurementsSnapshot(): MeasurementsSnapshot;
  // True when the adapter exposes a working raycast() — UI panel uses
  // this to render a "Raycasting non disponibile" warning when false.
  getRaycastAvailable(): boolean;
  // 4.I1: walk mode (FPV) controls + getter. Pointer-lock unsupported
  // / denied → entry rolls back automatically (see runWalkEffects).
  toggleWalk(): void;
  exitWalk(): void;
  getWalkSnapshot(): WalkSnapshot;
  // 4.K: WebXR (VR / AR) modal + session controls + getter. Modal
  // detection + session lifecycle are async; UI consumes snapshots
  // via 'xr-state' event. toggleXR is the convenience for the 'x'
  // shortcut / toolbar button (idle → openModal; modal-open →
  // closeModal; in-session → exitXR).
  toggleXR(): void;
  openXRModal(): void;
  closeXRModal(): void;
  enterXR(mode: XRMode): void;
  exitXR(): Promise<void>;
  getXRSnapshot(): XRSnapshot;
  // 4.M: device capabilities + walk-touch toast trigger.
  getDeviceCapabilities(): DeviceCapabilities;
  // Called by toolbar callback when the user clicks the walk button
  // on a touch device (button is aria-disabled but still clickable
  // for educational toast). Emits 'walk-error' reason='touch'.
  notifyUnavailableFeature(reason: 'walk-touch'): void;
  // 5.C: Studio Web author capture-camera. Returns an immutable
  // snapshot {cam_pos, cam_lookat, cam_fov} with finite-only values
  // and fov clamped to [10, 170]. Throws if the camera/controls
  // aren't wired yet (call only after 'viewer-ready').
  getCameraState(): {
    cam_pos: readonly [number, number, number];
    cam_lookat: readonly [number, number, number];
    cam_fov: number;
  };
  // 5.C: Studio Web author hotspot pos derivation. Casts a ray from
  // the viewport center forward against the loaded scene and returns
  // the first hit point as Vec3, or null if no hit / raycast not
  // available. Used to disable "Aggiungi hotspot" when there's no
  // model under the camera.
  raycastCenter(): readonly [number, number, number] | null;
  // Step 7: raycast da coordinate screen (canvas-relative, offsetX/Y).
  // Usato dal floorplan calibrator nello Studio editor per catturare
  // X/Z world quando l'autore clicca su un punto del modello 3D.
  raycastFromScreen(screenX: number, screenY: number): readonly [number, number, number] | null;
  // 5.C: Studio Web author CRUD sync. After create/update/delete via
  // the Studio API, call replaceHotspots/replacePresets so the
  // embedded viewer's sortedHotspots/sortedPresets cache stays in
  // sync (otherwise openHotspot/applyPreset would fail on new IDs).
  // Closes any popup/tour pointing to a removed entry.
  replaceHotspots(hotspots: readonly Hotspot[]): void;
  replacePresets(presets: readonly Preset[]): void;
}

interface InternalState {
  abortController: AbortController;
  unsubscribe: (() => void) | null;
  blobHandle: BlobUrlHandle | null;
  controls: OrbitControls | null;
  disposed: boolean;
  // Phase 6 — adaptive variant delivery state.
  manifest: import('../api/types.js').ModelManifest | null;
  variantOverride: import('../loader/variant-resolver.js').VariantOverride;
  // Retry attempt counter is persisted here so it survives the
  // error-transient → fetching-meta → ... cycle (the store state alone loses
  // it when RETRY moves out of error-transient).
  lastTransientAttempt: number;
  // 4.E: initial camera computed via adapter.computeFitCamera() when the
  // model finishes loading; used by resetView() to send the camera back.
  initialCamera: import('../viewer/adapter.js').CameraFit | null;
  // 4.E: perf HUD visibility (renderer also tracks; orchestrator authoritative).
  perfHudVisible: boolean;
  // 4.E: most recent ModelMeta filename for screenshot default name.
  lastFilename: string | null;
  // 4.E: cleanup handles for the bindings created in attachLoadedFeatures().
  loadedTeardown: (() => void)[];
  // 4.F: tour state-machine + animation ownership token. tourAbort gates which
  // flyToCamera invocation is "current"; manual applyPreset also uses it as a
  // local ownership token to avoid a race re-enabling controls during a
  // freshly-started tour transition.
  tourState: TourState;
  tourAbort: AbortController | null;
  sortedPresets: readonly Preset[];
  // 4.F: throttle ~10 hz emission of TICK no-op snapshots (dwell countdown).
  lastSnapshotAt: number;
  // 4.G: hotspots state + canonical sorted list + animation ownership token
  // separate from tourAbort (so an in-flight hotspot fly survives an
  // unrelated tour exit, and vice-versa).
  hotspotsState: HotspotsState;
  sortedHotspots: readonly Hotspot[];
  hotspotAbort: AbortController | null;
  // 4.H: floor plan minimap state. degenerateCalibration is computed
  // ONCE at scene-load and treated as immutable input; the reducer
  // never mutates it.
  floorplanState: FloorplanState;
  floorplan: FloorPlan | null;
  // Throttle the per-frame 'floorplan-position' emit to ~30 hz so the
  // ticker stays cheap even with N hotspots in the same loop.
  lastFloorplanEmit: number;
  // 4.J: measurements state + per-frame throttle + pointer counter
  // for deterministic point IDs (passed via event payload). The click
  // handler reference is kept so toggleMeasure can attach/detach it.
  measureState: MeasurementsState;
  lastMeasureProjEmit: number;
  measurePointSeq: number;
  measureClickHandler: ((ev: PointerEvent) => void) | null;
  // Step 7 #4 fix: paired pointerdown handler so we can detach on cleanup.
  measurePointerDownHandler?: (ev: PointerEvent) => void;
  // 4.I1: walk mode (FPV) state. Pointer-lock + key/mouse listeners +
  // RAF subscription owned by the orchestrator; reset on EXIT.
  walkState: WalkState;
  walkKeysHeld: Set<string>;
  walkYaw: number;
  walkPitch: number;
  walkVY: number;
  walkLastTickMs: number;
  walkJumpHeld: boolean;
  walkPointerLockHandler: (() => void) | null;
  walkKeyDownHandler: ((ev: KeyboardEvent) => void) | null;
  walkKeyUpHandler: ((ev: KeyboardEvent) => void) | null;
  walkMouseMoveHandler: ((ev: MouseEvent) => void) | null;
  walkBlurHandler: (() => void) | null;
  walkUnsubFrame: (() => void) | null;
  // 4.I1 Codex iter 1 code MINOR fix: incremented per pointer-lock
  // request so a stale Promise.reject from a previous entry doesn't
  // EXIT a fresh walk session (entry → exit → re-entry race).
  walkPointerLockSeq: number;
  // 4.K: WebXR (VR / AR) modal + session state. Adapter event
  // listeners are subscribed in attachLoadedFeatures so they survive
  // hot-reload of teardown/re-attach (see xrAdapterUnsub*). The
  // sequence guard `xrSessionSeq` invalidates the .catch of a stale
  // adapter.enterXR(mode) Promise after teardown / exitXR-while-
  // starting (Codex iter 4 plan MAJOR #1 fix).
  xrState: XRState;
  xrAdapterUnsubStarted: (() => void) | null;
  xrAdapterUnsubEnded: (() => void) | null;
  xrModalOpenerEl: HTMLElement | null;
  xrSessionSeq: number;
  // 4.M: device matcher subscription. Owned by orchestrator so the
  // unsubscribe runs in dispose() before the bootstrap tears down
  // the matcher itself.
  unsubDeviceMatcher: (() => void) | null;
}

function buildInitOptions(canvas: HTMLCanvasElement, embed: boolean): InitOptions {
  if (embed) {
    return {
      canvas,
      webXRMode: 'none',
      sharedMemoryForWorkers: false,
      gpuAcceleratedSort: false,
    };
  }
  return { canvas, webXRMode: 'none' };
}

function classifyMetaError(
  err: unknown,
  route: ParsedRoute,
): {
  type: 'ERROR_NOT_FOUND' | 'ERROR_EXPIRED' | 'ERROR_TRANSIENT';
  payload: Record<string, unknown>;
} {
  if (err instanceof ApiError) {
    if (err.code === 'not_found' || err.code === 'unauthorized' || err.code === 'forbidden') {
      return { type: 'ERROR_NOT_FOUND', payload: { route } };
    }
    if (err.code === 'gone_expired') {
      const expiresAt =
        typeof err.details?.['expires_at'] === 'number' ? (err.details['expires_at'] as number) : 0;
      return { type: 'ERROR_EXPIRED', payload: { route, expiresAt } };
    }
    return {
      type: 'ERROR_TRANSIENT',
      payload: { route, reason: err.code, canRetry: err.retryable },
    };
  }
  if (err instanceof NetworkError) {
    return { type: 'ERROR_TRANSIENT', payload: { route, reason: 'network', canRetry: true } };
  }
  return { type: 'ERROR_TRANSIENT', payload: { route, reason: 'unknown', canRetry: true } };
}

export function createOrchestrator(deps: OrchestratorDeps): Orchestrator {
  const events = new EventTarget();

  const internal: InternalState = {
    abortController: new AbortController(),
    unsubscribe: null,
    blobHandle: null,
    controls: null,
    disposed: false,
    manifest: null,
    variantOverride: null,
    lastTransientAttempt: 0,
    initialCamera: null,
    perfHudVisible: false,
    lastFilename: null,
    loadedTeardown: [],
    tourState: { kind: 'idle' },
    tourAbort: null,
    sortedPresets: [],
    lastSnapshotAt: 0,
    hotspotsState: { openIndex: -1, visible: false },
    sortedHotspots: [],
    hotspotAbort: null,
    floorplanState: { visible: false, imageError: false, degenerateCalibration: false },
    floorplan: null,
    lastFloorplanEmit: 0,
    measureState: { active: false, points: [], units: 'm' },
    lastMeasureProjEmit: 0,
    measurePointSeq: 0,
    measureClickHandler: null,
    walkState: { active: false, eyeHeight: EYE_HEIGHT, floorHeight: FLOOR_HEIGHT },
    walkKeysHeld: new Set<string>(),
    walkYaw: 0,
    walkPitch: 0,
    walkVY: 0,
    walkLastTickMs: 0,
    walkJumpHeld: false,
    walkPointerLockHandler: null,
    walkKeyDownHandler: null,
    walkKeyUpHandler: null,
    walkMouseMoveHandler: null,
    walkBlurHandler: null,
    walkUnsubFrame: null,
    walkPointerLockSeq: 0,
    xrState: INITIAL_XR_STATE,
    xrAdapterUnsubStarted: null,
    xrAdapterUnsubEnded: null,
    xrModalOpenerEl: null,
    xrSessionSeq: 0,
    unsubDeviceMatcher: null,
  };

  const emit = <K extends keyof OrchestratorEventDetailMap>(
    type: K,
    detail: OrchestratorEventDetailMap[K],
  ): void => {
    events.dispatchEvent(new CustomEvent(type, { detail }));
  };

  const revokeBlob = (): void => {
    if (internal.blobHandle !== null) {
      internal.blobHandle.revoke();
      internal.blobHandle = null;
    }
  };

  const bumpAttempt = (): number => {
    internal.lastTransientAttempt += 1;
    return internal.lastTransientAttempt;
  };

  const dispatchTransientError = (
    err: unknown,
    route: ParsedRoute | null,
    fallbackReason: string,
  ): void => {
    if (internal.disposed) return;
    if (route !== null) {
      const classified = classifyMetaError(err, route);
      if (classified.type === 'ERROR_NOT_FOUND') {
        deps.store.dispatch({ type: 'ERROR_NOT_FOUND', route });
        return;
      }
      if (classified.type === 'ERROR_EXPIRED') {
        deps.store.dispatch({
          type: 'ERROR_EXPIRED',
          route,
          expiresAt: classified.payload['expiresAt'] as number,
        });
        return;
      }
      deps.store.dispatch({
        type: 'ERROR_TRANSIENT',
        route,
        reason: (classified.payload['reason'] as string) ?? fallbackReason,
        canRetry: (classified.payload['canRetry'] as boolean) ?? true,
        attempt: bumpAttempt(),
      });
      return;
    }
    deps.store.dispatch({
      type: 'ERROR_TRANSIENT',
      route: null,
      reason: fallbackReason,
      canRetry: false,
      attempt: bumpAttempt(),
    });
  };

  const fetchMeta = async (route: ParsedRoute): Promise<void> => {
    const signal = internal.abortController.signal;
    try {
      // Phase 6 (parent plan §Fase 6 + Codex iter 2 NB#2): manifest call
      // separato. Fetch manifest first; if available pick variant per device
      // class via selectVariantForDevice; if 404/missing fall back to legacy
      // single-file fetchModel (no variant query). This keeps backward-compat
      // with pre-Phase-5 models that have no model_variants rows.
      let chosenVariant: 'hd' | 'sd' | undefined;
      let manifestWarning = '';
      try {
        const manifest = await deps.apiClient.fetchModelManifest(route.modelId, { signal });
        const deviceClass = detectDeviceClass(readDeviceClassInputs(deps.deviceMatcher.snapshot));
        const resolved = selectVariantForDevice(manifest, deviceClass, internal.variantOverride);
        if (resolved !== null && resolved.variant.variant !== 'source') {
          chosenVariant = resolved.variant.variant;
        }
        manifestWarning = resolved?.warning ?? '';
        internal.manifest = manifest;
      } catch (manifestErr) {
        if (isAbortError(manifestErr)) return;
        // Manifest 404 is expected for pre-Phase-5 models. Other errors
        // (5xx, network) we also tolerate here — fall back to legacy
        // fetchModel which yields the actionable error to the user.
        internal.manifest = null;
      }
      void manifestWarning; // reserved for future toolbar pill (Phase 6.3)

      const meta = await deps.apiClient.fetchModel(route.modelId, {
        signal,
        ...(chosenVariant !== undefined ? { variant: chosenVariant } : {}),
      });
      // Pre-hook expiry check (cheap, sync).
      if (meta.expires_at < Date.now()) {
        deps.store.dispatch({ type: 'ERROR_EXPIRED', route, expiresAt: meta.expires_at });
        return;
      }
      internal.lastFilename = meta.filename;

      // Step 7 Phase 0 — optional pre-load gate (mobile OOM guardrail).
      // Hook awaited can take arbitrary time (modal); guard post-await
      // for dispose/abort/expiry race conditions before dispatching
      // either META_FETCHED or LOAD_CANCELLED_BY_USER.
      if (deps.shouldProceedAfterMeta !== undefined) {
        let proceed: boolean;
        try {
          proceed = await deps.shouldProceedAfterMeta(meta, { signal });
        } catch (hookErr) {
          if (isAbortError(hookErr)) return;
          // Hook should never throw — log + treat as proceed (fail-open
          // so a buggy guardrail can't brick the viewer).
          console.warn('[orchestrator] shouldProceedAfterMeta threw:', hookErr);
          proceed = true;
        }
        // Post-await guards: orchestrator dispose, abort, or link
        // expiry while waiting for user response.
        if (internal.disposed || signal.aborted) return;
        if (meta.expires_at < Date.now()) {
          deps.store.dispatch({ type: 'ERROR_EXPIRED', route, expiresAt: meta.expires_at });
          return;
        }
        if (!proceed) {
          deps.store.dispatch({ type: 'LOAD_CANCELLED_BY_USER', route, meta });
          return;
        }
      }

      deps.store.dispatch({ type: 'META_FETCHED', route, meta });
    } catch (err) {
      if (isAbortError(err)) return;
      dispatchTransientError(err, route, 'meta-fetch-failed');
    }
  };

  const loadScene = async (route: ParsedRoute, meta: ModelMeta): Promise<void> => {
    const signal = internal.abortController.signal;

    const modelTask = (async (): Promise<void> => {
      // Revoke any prior blob (e.g. from a previous RETRY attempt) before
      // creating a new one — prevents one leaked blob per failed load.
      revokeBlob();

      // Phase 8 prod (2026-05-10): fallback SD→HD on mobile decode error.
      // SOG decode requires full WebGPU; iOS Safari's partial WebGPU
      // implementation rejects with a runtime exception that the orchestrator
      // classifies as reason:'unknown'. When the served variant is 'sd' AND
      // the adapter throws, retry once with the HD variant before failing.
      const isVariantMeta = (m: ModelMeta): m is ModelMetaVariant =>
        'variant_served' in m && m.variant_served !== undefined;
      const initialVariant = isVariantMeta(meta) ? meta.variant_served : null;

      const tryLoad = async (target: ModelMeta): Promise<BlobUrlHandle> => {
        const handle = await loadModelAsBlobUrl(target.download_url, {
          signal,
          onProgress: (p) => {
            deps.store.dispatch({ type: 'MODEL_PROGRESS', loaded: p.loaded, total: p.total });
          },
        });
        internal.blobHandle = handle;
        await deps.adapter.loadSplatScene(handle.blobUrl, { format: target.format });
        return handle;
      };

      try {
        await tryLoad(meta);
      } catch (err) {
        // Revoke the handle from the failed primary attempt before we try
        // the fallback. internal.blobHandle was set inside tryLoad.
        revokeBlob();

        // Eligible for SD→HD fallback?
        const eligible = initialVariant === 'sd' && !isAbortError(err);
        if (!eligible) throw err;

        try {
          const fallbackMeta = await deps.apiClient.fetchModel(route.modelId, {
            signal,
            variant: 'hd',
          });
          if (signal.aborted) throw err;
          console.warn(
            '[orchestrator] SD variant decode failed, retrying with HD',
            err instanceof Error ? err.message : String(err),
          );
          await tryLoad(fallbackMeta);
        } catch (fallbackErr) {
          // If the fallback handle was created and adapter still threw,
          // revoke it so we don't leak.
          if (internal.blobHandle !== null) {
            internal.blobHandle.revoke();
            internal.blobHandle = null;
          }
          // Surface the ORIGINAL error to the user (the SD failure they
          // care about); fallback failure is informational only.
          if (isAbortError(fallbackErr)) throw fallbackErr;
          throw err;
        }
      }
    })();

    const metadataTask = fetchMetadataTolerant(deps.apiClient, route.modelId, {
      signal,
      onPartial: (p) => {
        if (p.key === 'hotspots') {
          deps.store.dispatch({ type: 'METADATA_PARTIAL', key: 'hotspots', value: p.value });
        } else if (p.key === 'presets') {
          deps.store.dispatch({ type: 'METADATA_PARTIAL', key: 'presets', value: p.value });
        } else {
          deps.store.dispatch({ type: 'METADATA_PARTIAL', key: 'floorplan', value: p.value });
        }
      },
    });

    try {
      const [_, metadataResult] = await Promise.all([modelTask, metadataTask]);
      if (internal.disposed) return;
      // Ensure all metadata keys are present in the store before SCENE_LOADED;
      // the transition guards on complete metadata.
      ensureMetadataPresent(deps.store, metadataResult.metadata);
      for (const w of metadataResult.warnings) {
        console.warn(`[orchestrator] metadata warning: ${w.key} -> ${w.reason}`);
      }
      // 4.F: cache sorted presets BEFORE SCENE_LOADED dispatch. Store
      // subscribers (renderer) fire synchronously and read getSortedPresets()
      // when mounting Loaded; if we sorted only inside attachLoadedFeatures
      // the first mount would see an empty list and the pill would never
      // appear (mountPresetPill returns a no-op for length === 0).
      internal.sortedPresets = sortPresets(metadataResult.metadata.presets);
      // 4.G: same race-fix pattern — sortedHotspots must be present at
      // the first Loaded mount so dots/strip render with the correct count.
      internal.sortedHotspots = sortHotspots(metadataResult.metadata.hotspots);
      // Step 7 fix: same race-fix as floorplan below — initialize
      // hotspotsState.visible BEFORE dispatch(SCENE_LOADED) so the
      // renderer's first sync mount sees visible=true. Previously this
      // happened in attachLoadedFeatures AFTER dispatch, which mounted
      // mountHotspotDots with snapshot.visible=false → wrap.style.display
      // ='none' → dots invisible until the user toggled 'h'. The state
      // machine + ticker NEVER re-emit hotspot-state when the silent
      // flip happens, so the UI was stuck nascosto until manual toggle.
      if (internal.sortedHotspots.length > 0) {
        internal.hotspotsState = { openIndex: -1, visible: true };
      }
      // 4.H: floor plan + calibration degeneracy cached BEFORE dispatch
      // (same race-fix pattern as sortedPresets/sortedHotspots). Reducer
      // treats degenerateCalibration as immutable input. visible defaults
      // to true whenever a floorplan is attached so the renderer's first
      // sync mount sees the minimap (we used to flip this in
      // attachLoadedFeatures AFTER dispatch, which left the UI seeded
      // with visible=false until the next event — Codex iter 1 MAJOR fix).
      const fp = metadataResult.metadata.floorplan;
      internal.floorplan = fp;
      internal.floorplanState = {
        visible: fp !== null,
        imageError: false,
        degenerateCalibration: fp !== null && isCalibrationDegenerate(fp.calibration),
      };
      deps.store.dispatch({ type: 'SCENE_LOADED' });
    } catch (err) {
      if (isAbortError(err)) return;
      dispatchTransientError(err, route, 'scene-load-failed');
      // 5.C: also emit viewer-failure so the Studio embedded bridge's
      // ready Promise rejects (otherwise the editor stays in
      // loading-model forever).
      const message = err instanceof Error ? err.message : 'scene-load-failed';
      emit('viewer-failure', { message });
      return;
    }
    // 5.C: OrbitControls setup is required for capture-camera correctness
    // (getCameraState reads controls.target). On failure we still wire
    // loaded features (hotspot/preset/floorplan all work without
    // controls in fullscreen viewer + tests use fake adapters) but
    // emit `viewer-failure` instead of `viewer-ready` so the Studio
    // embedded bridge rejects `ready` and the editor goes to model-error.
    // The fullscreen viewer (src/main.ts) ignores viewer-failure.
    const controlsReady = attachOrbitControls();
    attachLoadedFeatures();
    if (controlsReady) {
      emit('viewer-ready', {});
    } else {
      emit('viewer-failure', { message: 'orbit-controls-failed' });
    }
  };

  const attachOrbitControls = (): boolean => {
    if (internal.disposed) return false;
    try {
      const camera = deps.adapter.getCamera();
      const renderer = deps.adapter.getRenderer();
      internal.controls = createOrbitControls(camera, renderer.domElement);
      if (canSetBeforeRender(deps.adapter)) {
        const controls = internal.controls;
        // 4.I1 Codex iter 1 code MAJOR fix: skip update() when controls
        // are disabled (e.g. during walk mode FPV). mkkellogg's
        // OrbitControls.update() can drift the camera toward its stored
        // `target` even with enabled=false, fighting the per-frame
        // setCamera() walkTick performs.
        deps.adapter.setBeforeRender(() => {
          if (controls.enabled) controls.update();
        });
      }
      return true;
    } catch (err) {
      console.warn('[orchestrator] OrbitControls setup failed:', err);
      return false;
    }
  };

  // ============================================================
  // 4.F Tour wiring (camera presets pill + guided tour state machine)
  // ============================================================
  //
  // All mutations to internal.tourState pass through dispatchTourEvent so
  // effect scheduling (flyToCamera, abort, controls toggle, snapshot emit)
  // is deterministic. The pure SM (tourTransition) decides the next state;
  // runTransitionEffects fires the side effects that match the (prev, next,
  // event) triple. See STEP4F_PLAN.md "Unified dispatch + effect scheduler".

  const currentCamera = (): CameraTarget => {
    const camera = deps.adapter.getCamera();
    const target = internal.controls?.target;
    return {
      pos: [camera.position.x, camera.position.y, camera.position.z],
      lookAt: target ? [target.x, target.y, target.z] : [0, 0, 0],
      fov: camera.fov,
    };
  };

  const presetCamera = (p: Preset): CameraTarget => ({
    pos: p.cam_pos as [number, number, number],
    lookAt: p.cam_lookat as [number, number, number],
    fov: p.cam_fov,
  });

  const currentTourIndex = (): number => {
    const s = internal.tourState;
    if (s.kind === 'dwelling') return s.index;
    if (s.kind === 'transitioning') return s.to;
    if (s.kind === 'paused') return s.index;
    return -1;
  };

  const nowFromEvent = (e: TourEvent): number => (e.type === 'EXIT' ? performance.now() : e.now);

  const maybeEmitSnapshot = (state: TourState, now: number): void => {
    if (state.kind === 'idle') return;
    if (now - internal.lastSnapshotAt < 100) return;
    internal.lastSnapshotAt = now;
    emit('tour-state', tourSnapshot(state, internal.sortedPresets, now));
  };

  const runTransitionEffects = (prev: TourState, next: TourState, event: TourEvent): void => {
    // Step 1: abort any active flyTo when starting a NEW transitioning, or
    // when leaving the active state via PAUSE/EXIT.
    const newTransitionStarting =
      next.kind === 'transitioning' &&
      !(prev.kind === 'transitioning' && prev.to === next.to && prev.startedAt === next.startedAt);
    if (newTransitionStarting || next.kind === 'idle' || next.kind === 'paused') {
      internal.tourAbort?.abort();
      internal.tourAbort = null;
    }

    // Step 2: kick off the new flyToCamera. RESUME shrinks duration to
    // remainingMs so the animation ends exactly when the SM exits
    // transitioning; START/GOTO/end-of-dwell use TRANSITION_MS full.
    if (newTransitionStarting && next.kind === 'transitioning') {
      const fromCam = currentCamera();
      const toCam = presetCamera(internal.sortedPresets[next.to] as Preset);
      const isResume = event.type === 'RESUME';
      // 4.M: prefers-reduced-motion → snap immediate (durationMs=0).
      const reducedMotion = deps.deviceMatcher.snapshot.reducedMotion;
      const durationMs = reducedMotion
        ? 0
        : isResume &&
            prev.kind === 'paused' &&
            prev.pausedFromKind === 'transitioning' &&
            prev.index === next.to
          ? prev.remainingMs
          : TRANSITION_MS;
      if (internal.controls !== null) internal.controls.enabled = false;
      const controller = new AbortController();
      internal.tourAbort = controller;
      void flyToCamera(deps.adapter, internal.controls, fromCam, toCam, {
        durationMs,
        signal: controller.signal,
      })
        .catch((err) => {
          if (!isAbortError(err)) console.warn('[tour] flyTo failed:', err);
        })
        .finally(() => {
          // Re-enable controls only if WE are still the owner AND SM has
          // settled into dwelling for THIS index. Otherwise a newer transition
          // owns controls (or tour exited) — leave them alone.
          if (
            internal.tourAbort === controller &&
            internal.tourState.kind === 'dwelling' &&
            internal.tourState.index === next.to &&
            internal.controls !== null
          ) {
            internal.controls.enabled = true;
          }
        });
    }

    // Step 3: idempotent re-enable on dwelling/idle (catch-all).
    if ((next.kind === 'dwelling' || next.kind === 'idle') && internal.controls !== null) {
      internal.controls.enabled = true;
    }
  };

  // ============================================================
  // 4.G Hotspots wiring (dot scatter + popup card + strip)
  // ============================================================
  //
  // Mirrors the 4.F dispatch pattern: a single helper mutates state, fires
  // side effects (flyTo + abort + controls toggle), and emits the snapshot.
  // Hotspot ownership uses a separate AbortController (`internal.hotspotAbort`)
  // so an in-flight tour fly survives an unrelated hotspot exit and vice-versa.

  const runHotspotEffects = (
    prev: HotspotsState,
    next: HotspotsState,
    _event: HotspotsEvent,
  ): void => {
    const isFlyingToNew = next.openIndex !== -1 && next.openIndex !== prev.openIndex;
    if (isFlyingToNew) {
      // Tour ticker would otherwise resume control over the camera. Exit
      // before fly so the hotspot animation owns the controls cleanly.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      internal.hotspotAbort?.abort();
      const target = internal.sortedHotspots[next.openIndex];
      if (target === undefined) return;
      const cur = currentCamera();
      const to: CameraTarget = {
        pos: target.cam_pos as [number, number, number],
        lookAt: target.cam_lookat as [number, number, number],
        fov: target.cam_fov,
      };
      if (internal.controls !== null) internal.controls.enabled = false;
      const ctrl = new AbortController();
      internal.hotspotAbort = ctrl;
      // 4.M: prefers-reduced-motion → snap immediate.
      const hotspotDuration = deps.deviceMatcher.snapshot.reducedMotion ? 0 : TRANSITION_MS;
      void flyToCamera(deps.adapter, internal.controls, cur, to, {
        durationMs: hotspotDuration,
        signal: ctrl.signal,
      })
        .catch((err) => {
          if (!isAbortError(err)) console.warn('[hotspot] flyTo failed:', err);
        })
        .finally(() => {
          // Token check: only re-enable controls if we are still the active
          // owner (a newer hotspot fly or a closeHotspot may have aborted us).
          if (internal.hotspotAbort === ctrl && internal.controls !== null) {
            internal.controls.enabled = true;
          }
        });
    }
    // Closing → abort any in-flight fly; user keeps the current view (no
    // camera revert).
    if (next.openIndex === -1 && prev.openIndex !== -1) {
      internal.hotspotAbort?.abort();
      internal.hotspotAbort = null;
      if (internal.controls !== null) internal.controls.enabled = true;
    }
  };

  const dispatchHotspotEvent = (event: HotspotsEvent): void => {
    if (internal.disposed) return;
    const prev = internal.hotspotsState;
    const next = hotspotsTransition(prev, event, internal.sortedHotspots.length);
    if (next === prev) return;
    internal.hotspotsState = next;
    runHotspotEffects(prev, next, event);
    emit('hotspot-state', hotspotsSnapshot(next, internal.sortedHotspots));
  };

  // ============================================================
  // 4.H Floor plan minimap wiring
  // ============================================================
  //
  // Same dispatch pattern as 4.F/4.G: a single helper mutates state and
  // emits the snapshot. Teleport is a side effect (not an event in the
  // SM) because it changes the viewer camera, not the minimap state —
  // it's modeled the same way as resetView (4.E) and applyPreset idle
  // path (4.F).

  const dispatchFloorplanEvent = (event: FloorplanEvent): void => {
    if (internal.disposed) return;
    const prev = internal.floorplanState;
    const next = floorplanTransition(prev, event);
    if (next === prev) return;
    internal.floorplanState = next;
    emit('floorplan-state', floorplanSnapshot(next));
  };

  const teleportOnFloorplan = (worldXZ: readonly [number, number]): void => {
    if (internal.disposed) return;
    if (internal.floorplan === null) return;
    if (internal.floorplanState.degenerateCalibration) return; // ignored on degenerate
    // UX guard mirrored from hotspot.openHotspot — a hard camera shift
    // makes the popup point to a stale view.
    if (internal.tourState.kind !== 'idle') {
      dispatchTourEvent({ type: 'EXIT' });
    }
    if (internal.hotspotsState.openIndex !== -1) {
      dispatchHotspotEvent({ type: 'CLOSE' });
    }
    let cur: CameraTarget;
    try {
      cur = currentCamera();
    } catch (err) {
      console.warn('[floorplan] currentCamera failed:', err);
      return;
    }
    const offset: [number, number, number] = [
      cur.lookAt[0] - cur.pos[0],
      cur.lookAt[1] - cur.pos[1],
      cur.lookAt[2] - cur.pos[2],
    ];
    const newPos: [number, number, number] = [worldXZ[0], cur.pos[1], worldXZ[1]];
    const newLookAt: [number, number, number] = [
      newPos[0] + offset[0],
      newPos[1] + offset[1],
      newPos[2] + offset[2],
    ];
    try {
      deps.adapter.setCamera(newPos, newLookAt, cur.fov);
      if (internal.controls !== null) {
        internal.controls.target.set(newLookAt[0], newLookAt[1], newLookAt[2]);
        internal.controls.update();
      }
    } catch (err) {
      console.warn('[floorplan] teleport failed:', err);
    }
  };

  const dispatchTourEvent = (event: TourEvent): void => {
    if (internal.disposed) return;
    const prev = internal.tourState;
    const next = tourTransition(prev, event, internal.sortedPresets.length);
    if (next === prev) {
      if (event.type === 'TICK') maybeEmitSnapshot(prev, event.now);
      return;
    }
    internal.tourState = next;
    runTransitionEffects(prev, next, event);
    emit('tour-state', tourSnapshot(next, internal.sortedPresets, nowFromEvent(event)));
  };

  // ============================================================
  // 4.J Measurements wiring
  // ============================================================
  //
  // Same dispatch + effects pattern as 4.F/4.G/4.H. Side effects on the
  // active flip (install/remove canvas click handler, exit tour first)
  // live in runMeasurementEffects so they're driven by the toggle, not
  // by the first click — see plan iter 1 MAJOR #3 fix.

  // Step 7 #4 fix: distinguish "click pick" from "drag rotate". Without
  // this every release of the mouse button after an OrbitControls drag
  // adds a measure point — the canvas DOM 'click' event fires even for
  // small drag deltas. Industry-standard golden pattern (Matterport,
  // Sketchfab, Polycam, Esri ArcGIS): track pointerdown coordinates,
  // only pick on pointerup if the cursor moved less than ~5px.
  const MEASURE_DRAG_THRESHOLD_PX = 5;
  const installMeasureClickHandler = (): void => {
    if (internal.measureClickHandler !== null) return;
    let downX = 0;
    let downY = 0;
    let downValid = false;
    const onDown = (ev: PointerEvent): void => {
      // Only consider primary button (left click / single touch). On
      // mouse: button 0. On touch/pen: ev.button is also 0.
      if (ev.button !== 0) {
        downValid = false;
        return;
      }
      downX = ev.clientX;
      downY = ev.clientY;
      downValid = true;
    };
    const onUp = (ev: PointerEvent): void => {
      if (!downValid) return;
      downValid = false;
      const dx = ev.clientX - downX;
      const dy = ev.clientY - downY;
      const moved = Math.hypot(dx, dy);
      if (moved >= MEASURE_DRAG_THRESHOLD_PX) {
        // Treat as drag (orbit / pan); do not pick.
        return;
      }
      // offsetX/Y on pointerup is reliable — translates client coords to
      // canvas-relative coords used by the raycaster.
      api.addMeasurePoint(ev.offsetX, ev.offsetY);
    };
    internal.measureClickHandler = onUp;
    internal.measurePointerDownHandler = onDown;
    deps.canvas.addEventListener('pointerdown', onDown);
    deps.canvas.addEventListener('pointerup', onUp);
    deps.canvas.style.cursor = 'crosshair';
  };

  const removeMeasureClickHandler = (): void => {
    if (internal.measureClickHandler === null) return;
    deps.canvas.removeEventListener('pointerup', internal.measureClickHandler);
    if (internal.measurePointerDownHandler !== undefined) {
      deps.canvas.removeEventListener('pointerdown', internal.measurePointerDownHandler);
      delete internal.measurePointerDownHandler;
    }
    internal.measureClickHandler = null;
    deps.canvas.style.cursor = '';
  };

  const runMeasurementEffects = (
    prev: MeasurementsState,
    next: MeasurementsState,
    _event: MeasurementsEvent,
  ): void => {
    if (!prev.active && next.active) {
      // Tour ticker would otherwise keep moving the camera under the
      // user's first click. Exit tour BEFORE attaching the click handler.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      installMeasureClickHandler();
    }
    if (prev.active && !next.active) {
      removeMeasureClickHandler();
    }
  };

  const dispatchMeasurementEvent = (event: MeasurementsEvent): void => {
    if (internal.disposed) return;
    const prev = internal.measureState;
    const next = measurementsTransition(prev, event);
    if (next === prev) return;
    internal.measureState = next;
    runMeasurementEffects(prev, next, event);
    emit('measurement-state', measurementsSnapshot(next));
  };

  // ============================================================
  // 4.I1 Walk Mode (FPV) wiring
  // ============================================================
  //
  // dispatchWalkEvent funnels every state mutation; runWalkEffects
  // owns side effects (pointer-lock attach/detach, key/mouse listener
  // install, frame ticker subscription, OrbitControls toggle, exit
  // tour/hotspot/measure first). Pointer-lock is fragile (browser
  // unsupported, iframe permission denial) — we handle both sync and
  // async failures by dispatching a nested EXIT, and the outer
  // dispatch guards against emitting a stale active=true snapshot.

  const installWalkInputHandlers = (): void => {
    if (internal.walkKeyDownHandler !== null) return;
    const onKeyDown = (ev: KeyboardEvent): void => {
      const key = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
      // Only consume when walk is active; SHORTCUTS handler still owns 'v'.
      if (!internal.walkState.active) return;
      // Prevent default for movement keys + Space so the page doesn't scroll.
      if (key === 'w' || key === 'a' || key === 's' || key === 'd' || key === ' ') {
        ev.preventDefault();
      }
      internal.walkKeysHeld.add(key);
    };
    const onKeyUp = (ev: KeyboardEvent): void => {
      const key = ev.key.length === 1 ? ev.key.toLowerCase() : ev.key;
      if (!internal.walkState.active) return;
      internal.walkKeysHeld.delete(key);
      if (key === ' ') internal.walkJumpHeld = false;
    };
    const onMouseMove = (ev: MouseEvent): void => {
      if (!internal.walkState.active) return;
      // Only consume mouse delta when pointer is actually locked.
      if (document.pointerLockElement !== deps.canvas) return;
      // movementX positive = mouse moved right → user wants to look right
      // → in our convention (yaw positive = LEFT), yaw decreases.
      internal.walkYaw -= ev.movementX * MOUSE_SENSITIVITY;
      internal.walkPitch = clampPitch(internal.walkPitch - ev.movementY * MOUSE_SENSITIVITY);
    };
    const onPointerLockChange = (): void => {
      // Browser ESC / tab switch / programmatic exitPointerLock all fire
      // pointerlockchange. If we lost the lock while still active, dispatch
      // EXIT so the SM rolls back cleanly.
      if (document.pointerLockElement !== deps.canvas && internal.walkState.active) {
        dispatchWalkEvent({ type: 'EXIT' });
      }
    };
    const onBlur = (): void => {
      // Window lost focus (alt-tab, cmd-tab) — keyup events won't arrive
      // for keys held down at the moment of blur. Clear the held set so
      // the next keydown starts from a clean state.
      internal.walkKeysHeld.clear();
      internal.walkJumpHeld = false;
    };
    internal.walkKeyDownHandler = onKeyDown;
    internal.walkKeyUpHandler = onKeyUp;
    internal.walkMouseMoveHandler = onMouseMove;
    internal.walkPointerLockHandler = onPointerLockChange;
    internal.walkBlurHandler = onBlur;
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('pointerlockchange', onPointerLockChange);
    window.addEventListener('blur', onBlur);
  };

  const removeWalkInputHandlers = (): void => {
    if (internal.walkKeyDownHandler !== null) {
      window.removeEventListener('keydown', internal.walkKeyDownHandler);
      internal.walkKeyDownHandler = null;
    }
    if (internal.walkKeyUpHandler !== null) {
      window.removeEventListener('keyup', internal.walkKeyUpHandler);
      internal.walkKeyUpHandler = null;
    }
    if (internal.walkMouseMoveHandler !== null) {
      document.removeEventListener('mousemove', internal.walkMouseMoveHandler);
      internal.walkMouseMoveHandler = null;
    }
    if (internal.walkPointerLockHandler !== null) {
      document.removeEventListener('pointerlockchange', internal.walkPointerLockHandler);
      internal.walkPointerLockHandler = null;
    }
    if (internal.walkBlurHandler !== null) {
      window.removeEventListener('blur', internal.walkBlurHandler);
      internal.walkBlurHandler = null;
    }
    internal.walkKeysHeld.clear();
    internal.walkJumpHeld = false;
  };

  const snapCameraToWalkStart = (): void => {
    try {
      const cam = deps.adapter.getCamera();
      // Extract yaw from current camera quaternion in YXZ order so we
      // preserve the user's current heading on entry.
      const eulerCtor = (deps.adapter.getCamera as unknown as { Euler?: unknown }).Euler;
      // Three.js: extract yaw via Euler.setFromQuaternion(quat, 'YXZ').y.
      // We import THREE.Euler dynamically to avoid pulling all of three at
      // the top of the file (already pulled by other modules).
      // Inline workaround using a small math: yaw = atan2(2(qw*qy + qx*qz), 1 - 2(qy² + qz²))
      const q = cam.quaternion;
      const yaw = Math.atan2(2 * (q.w * q.y + q.x * q.z), 1 - 2 * (q.y * q.y + q.z * q.z));
      internal.walkYaw = yaw;
      internal.walkPitch = 0;
      internal.walkVY = 0;
      internal.walkLastTickMs = performance.now();
      internal.walkJumpHeld = false;
      const newY = internal.walkState.floorHeight + internal.walkState.eyeHeight;
      const fwd = forwardVector(yaw, 0);
      deps.adapter.setCamera(
        [cam.position.x, newY, cam.position.z],
        [cam.position.x + fwd[0], newY + fwd[1], cam.position.z + fwd[2]],
        cam.fov,
      );
      // Suppress unused var
      void eulerCtor;
    } catch (err) {
      console.warn('[walk] snap to start failed:', err);
    }
  };

  const walkTick = (now: number): void => {
    if (!internal.walkState.active) return;
    const dt = Math.min(0.1, (now - internal.walkLastTickMs) / 1000);
    internal.walkLastTickMs = now;
    if (dt <= 0) return;
    let cam: import('three').PerspectiveCamera;
    try {
      cam = deps.adapter.getCamera();
    } catch {
      return;
    }
    // XZ movement.
    const { dx, dz } = computeVelocity(internal.walkKeysHeld, internal.walkYaw, WALK_SPEED);
    const newX = cam.position.x + dx * dt;
    const newZ = cam.position.z + dz * dt;
    // Y gravity + floor clamp.
    const floorEye = internal.walkState.floorHeight + internal.walkState.eyeHeight;
    const grav = applyGravity(cam.position.y, internal.walkVY, dt, floorEye);
    internal.walkVY = grav.vY;
    // Jump (latched: requires keyup + re-press to fire again).
    const onFloorNow = grav.onFloor || Math.abs(grav.y - floorEye) < ON_FLOOR_EPSILON;
    if (internal.walkKeysHeld.has(' ') && !internal.walkJumpHeld && onFloorNow) {
      internal.walkVY = JUMP_VELOCITY;
      internal.walkJumpHeld = true;
    }
    // Apply position + lookAt.
    const fwd = forwardVector(internal.walkYaw, internal.walkPitch);
    try {
      deps.adapter.setCamera(
        [newX, grav.y, newZ],
        [newX + fwd[0], grav.y + fwd[1], newZ + fwd[2]],
        cam.fov,
      );
    } catch (err) {
      console.warn('[walk] setCamera in tick failed:', err);
    }
  };

  const runWalkEffects = (prev: WalkState, next: WalkState, _event: WalkEvent): void => {
    if (!prev.active && next.active) {
      // Exit other UI states first — camera must be free for FPV control.
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      if (internal.hotspotsState.openIndex !== -1) dispatchHotspotEvent({ type: 'CLOSE' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      // Pointer-lock acquisition: defensive against unsupported APIs.
      // 4.M: emit 'walk-error' alongside the EXIT so the UI can show
      // a contextual toast (reason discriminator + embed flag).
      if (typeof deps.canvas.requestPointerLock !== 'function') {
        console.warn('[walk] pointer lock API not available; aborting');
        emit('walk-error', { reason: 'unsupported', embed: deps.embed });
        dispatchWalkEvent({ type: 'EXIT' });
        return;
      }
      if (internal.controls !== null) internal.controls.enabled = false;
      snapCameraToWalkStart();
      installWalkInputHandlers();
      deps.canvas.style.cursor = 'none';
      // Subscribe per-frame ticker (piggybacks on the same RAF the FPS
      // monitor already uses; cleanup in removeWalkFrameSub).
      if (canSubscribeFrames(deps.adapter)) {
        internal.walkUnsubFrame = deps.adapter.onFrame((now) => walkTick(now));
      }
      // Bump the sequence so an in-flight Promise.reject from a previous
      // entry can be discriminated (Codex iter 1 code MINOR fix).
      internal.walkPointerLockSeq += 1;
      const seq = internal.walkPointerLockSeq;
      let lockResult: unknown;
      try {
        lockResult = deps.canvas.requestPointerLock();
      } catch (err) {
        console.warn('[walk] pointer lock threw synchronously:', err);
        emit('walk-error', { reason: 'pointer-lock-failed', embed: deps.embed });
        dispatchWalkEvent({ type: 'EXIT' });
        return;
      }
      if (
        lockResult !== undefined &&
        lockResult !== null &&
        typeof (lockResult as Promise<unknown>).then === 'function'
      ) {
        (lockResult as Promise<unknown>).catch((err) => {
          // Stale reject guard: only roll back if THIS request is still
          // the latest one AND walk is currently active. Otherwise an
          // entry → exit → re-entry race would let the first reject
          // deactivate the new session — and emit a stale toast (Codex
          // iter 3 plan addition #1: walk-error stays under the same
          // sequence guard).
          if (internal.walkPointerLockSeq !== seq) return;
          if (!internal.walkState.active) return;
          console.warn('[walk] pointer lock denied:', err);
          emit('walk-error', { reason: 'pointer-lock-failed', embed: deps.embed });
          dispatchWalkEvent({ type: 'EXIT' });
        });
      }
    }
    if (prev.active && !next.active) {
      if (
        typeof document.exitPointerLock === 'function' &&
        document.pointerLockElement === deps.canvas
      ) {
        document.exitPointerLock();
      }
      if (internal.walkUnsubFrame !== null) {
        internal.walkUnsubFrame();
        internal.walkUnsubFrame = null;
      }
      removeWalkInputHandlers();
      deps.canvas.style.cursor = '';
      // Codex iter 1 code MAJOR fix: realign controls.target to the
      // current camera lookAt BEFORE re-enabling. OrbitControls.update()
      // reads the stored target and would otherwise snap the camera
      // back toward the pre-walk view on the very next frame.
      if (internal.controls !== null) {
        try {
          const cam = deps.adapter.getCamera();
          const fwd = forwardVector(internal.walkYaw, internal.walkPitch);
          internal.controls.target.set(
            cam.position.x + fwd[0],
            cam.position.y + fwd[1],
            cam.position.z + fwd[2],
          );
        } catch (err) {
          console.warn('[walk] failed to realign controls.target on exit:', err);
        }
        internal.controls.enabled = true;
      }
    }
  };

  const dispatchWalkEvent = (event: WalkEvent): void => {
    if (internal.disposed) return;
    const prev = internal.walkState;
    const next = walkTransition(prev, event);
    if (next === prev) return;
    internal.walkState = next;
    runWalkEffects(prev, next, event);
    // Codex iter 2 plan MAJOR fix: runWalkEffects may dispatch a nested
    // EXIT (e.g. pointer-lock unsupported / sync throw → rollback). The
    // nested dispatch already updated internal.walkState + emitted its
    // own snapshot. We MUST NOT emit `next` again here, or the renderer
    // would see a stale active=true after the actual rollback.
    if (internal.walkState !== next) return;
    emit('walk-state', walkSnapshot(next));
  };

  // ============================================================
  // 4.K WebXR (VR / AR) wiring
  // ============================================================
  //
  // dispatchXREvent funnels every state mutation through xrTransition;
  // runXREffects schedules side effects (detection async, exit other
  // modes, adapter.enterXR, adapter.exitXR). The orchestrator owns
  // a `xrSessionSeq` guard so a stale adapter.enterXR(mode) Promise
  // .catch — e.g. resolved after teardown / exitXR-while-starting —
  // is swallowed instead of re-emitting an ERROR snapshot post
  // cleanup (Codex iter 4 plan MAJOR #1 fix).

  const runXREffects = (prev: XRState, next: XRState, _event: XREvent): void => {
    // OPEN_MODAL transition: kick off async detection. Capture the
    // currently-focused element so we can restore focus on close.
    if (!prev.modalOpen && next.modalOpen && prev.status !== 'error') {
      const active = (
        typeof document !== 'undefined' ? document.activeElement : null
      ) as HTMLElement | null;
      internal.xrModalOpenerEl = active;
      if (!canEnterXR(deps.adapter)) {
        dispatchXREvent({ type: 'ERROR', message: 'Adapter senza supporto WebXR' });
        return;
      }
      internal.xrSessionSeq += 1;
      const seq = internal.xrSessionSeq;
      void detectXRSupport()
        .then((support) => {
          if (internal.disposed) return;
          if (internal.xrSessionSeq !== seq) return; // stale
          dispatchXREvent({ type: 'DETECT_RESULT', support });
        })
        .catch((err: unknown) => {
          if (internal.disposed) return;
          if (internal.xrSessionSeq !== seq) return;
          const msg = err instanceof Error ? err.message : 'Detection fallita';
          dispatchXREvent({ type: 'ERROR', message: msg });
        });
    }

    // CLOSE_MODAL: restore focus to opener (best-effort; opener may
    // have been removed from DOM in the meantime).
    if (prev.modalOpen && !next.modalOpen && next.status !== 'in-session') {
      const opener = internal.xrModalOpenerEl;
      internal.xrModalOpenerEl = null;
      if (opener !== null) {
        try {
          opener.focus();
        } catch {
          // ignore stale element
        }
      }
    }

    // SESSION_REQUEST: exit other modes first, then call adapter.
    if (prev.status !== 'starting' && next.status === 'starting' && next.activeMode !== null) {
      if (internal.tourState.kind !== 'idle') dispatchTourEvent({ type: 'EXIT' });
      if (internal.hotspotsState.openIndex !== -1) dispatchHotspotEvent({ type: 'CLOSE' });
      if (internal.measureState.active) dispatchMeasurementEvent({ type: 'EXIT' });
      if (internal.walkState.active) dispatchWalkEvent({ type: 'EXIT' });

      if (!canEnterXR(deps.adapter)) {
        dispatchXREvent({ type: 'ERROR', message: 'Adapter senza supporto WebXR' });
        return;
      }
      internal.xrSessionSeq += 1;
      const seq = internal.xrSessionSeq;
      const mode = next.activeMode;
      void deps.adapter.enterXR(mode).catch((err: unknown) => {
        if (internal.disposed) return;
        if (internal.xrSessionSeq !== seq) return;
        const msg = err instanceof Error ? err.message : 'Avvio sessione fallito';
        dispatchXREvent({ type: 'ERROR', message: msg });
      });
      // SESSION_STARTED dispatch arrives via adapter 'xr-session-started'
      // listener installed in attachLoadedFeatures.
    }
  };

  const dispatchXREvent = (event: XREvent): void => {
    if (internal.disposed) return;
    const prev = internal.xrState;
    const next = xrTransition(prev, event);
    if (next === prev) return;
    internal.xrState = next;
    runXREffects(prev, next, event);
    // Same guard as dispatchWalkEvent (Codex iter 2 plan MAJOR #2 fix
    // for walk → reused here): runXREffects can dispatch a nested
    // event (e.g. ERROR after capability check fails synchronously).
    // The nested dispatch already updated internal.xrState + emitted
    // its snapshot. We MUST NOT emit `next` again or the consumer
    // sees a stale snapshot.
    if (internal.xrState !== next) return;
    emit('xr-state', xrSnapshot(next));
  };

  const attachLoadedFeatures = (): void => {
    if (internal.disposed) return;
    // 4.L: if the URL carries a shared camera, decode it and apply as initial
    // view (skip auto-fit). Falls through to auto-fit on invalid encoding.
    const state = deps.store.getState();
    const route = state.kind === 'loaded' ? state.route : null;
    if (route?.cameraEncoded != null && route.cameraEncoded.length > 0) {
      const decoded = decodeCameraState(route.cameraEncoded);
      if (decoded !== null) {
        try {
          internal.initialCamera = decoded;
          deps.adapter.setCamera(decoded.pos, decoded.lookAt, decoded.fov);
          if (internal.controls !== null) {
            internal.controls.target.set(decoded.lookAt[0], decoded.lookAt[1], decoded.lookAt[2]);
            internal.controls.update();
          }
        } catch (err) {
          console.warn('[orchestrator] shared camera apply failed:', err);
          internal.initialCamera = null;
        }
      } else {
        console.warn('[orchestrator] cameraEncoded invalid, falling back to auto-fit');
      }
    }
    // 4.E: compute auto-fit camera and apply as initial view (also reset target)
    // ONLY if no shared camera was decoded above.
    if (internal.initialCamera === null && canFitCamera(deps.adapter)) {
      try {
        const fit = deps.adapter.computeFitCamera();
        if (fit !== null) {
          internal.initialCamera = fit;
          deps.adapter.setCamera(fit.pos, fit.lookAt, fit.fov);
          if (internal.controls !== null) {
            internal.controls.target.set(fit.lookAt[0], fit.lookAt[1], fit.lookAt[2]);
            internal.controls.update();
          }
        }
      } catch (err) {
        console.warn('[orchestrator] auto-fit failed (non-fatal):', err);
      }
    }

    // 4.F: sortedPresets is pre-cached in loadScene() before SCENE_LOADED so
    // the renderer's first Loaded mount sees presets immediately. We don't
    // re-sort here.

    // Step 7 fix: hotspotsState.visible is now initialized in loadScene()
    // BEFORE dispatch(SCENE_LOADED) — same race-fix pattern as floorplan.
    // The flip here AFTER dispatch left the renderer mounted with
    // visible=false and never re-emitted hotspot-state.

    // 4.H: floorplanState.visible is pre-set in loadScene() before
    // SCENE_LOADED so the renderer's first Loaded mount sees it
    // immediately. Even with degenerate calibration the minimap is
    // mounted (the ticker below skips position emission in that case).

    // 4.E + 4.F + 4.G: FPS monitor + tour ticker + hotspot positions share
    // the per-frame subscription.
    if (canSubscribeFrames(deps.adapter)) {
      const monitor = createFpsMonitor();
      let frameCounter = 0;
      const unsubFrame = deps.adapter.onFrame((now) => {
        monitor.tick(now);
        // Throttle fps-update emission to ~10 hz (every 6 frames).
        frameCounter += 1;
        if (frameCounter % 6 === 0) {
          emit('fps-update', { fps: monitor.getCurrentFps() });
        }
        // 4.F: drive tour state machine.
        dispatchTourEvent({ type: 'TICK', now });
        // 4.G: emit hotspot screen positions only when the UI needs them.
        // Cleanup contract: skip the entire .map() and the emit when not
        // visible OR no hotspots — no allocations, no listener noise.
        if (internal.hotspotsState.visible && internal.sortedHotspots.length > 0) {
          const camera = deps.adapter.getCamera();
          const viewport = {
            width: deps.canvas.clientWidth,
            height: deps.canvas.clientHeight,
          };
          const positions: HotspotScreenPosition[] = internal.sortedHotspots.map((h, idx) => ({
            idx,
            ...projectWorldToScreen(h.pos, camera, viewport),
          }));
          emit('hotspot-positions', { positions });
        }
        // 4.J: emit measurement projections (per-point screen coords +
        // cumulative segment distances) throttled to ~30 hz. Cleanup
        // contract: skip when measure mode is off OR no points yet —
        // no allocations, no listener noise.
        if (
          internal.measureState.active &&
          internal.measureState.points.length > 0 &&
          now - internal.lastMeasureProjEmit >= 33
        ) {
          internal.lastMeasureProjEmit = now;
          try {
            const camera = deps.adapter.getCamera();
            const viewport = {
              width: deps.canvas.clientWidth,
              height: deps.canvas.clientHeight,
            };
            const projections: MeasurementProjection[] = internal.measureState.points.map(
              (p, idx) => {
                const proj = projectWorldToScreen(p.world, camera, viewport);
                return {
                  idx,
                  screenX: proj.x,
                  screenY: proj.y,
                  visible: proj.visible,
                };
              },
            );
            const distances: number[] = [];
            for (let i = 0; i < internal.measureState.points.length - 1; i++) {
              const a = internal.measureState.points[i];
              const b = internal.measureState.points[i + 1];
              if (a !== undefined && b !== undefined) {
                const dx = b.world[0] - a.world[0];
                const dy = b.world[1] - a.world[1];
                const dz = b.world[2] - a.world[2];
                distances.push(Math.sqrt(dx * dx + dy * dy + dz * dz));
              }
            }
            emit('measurement-projections', { projections, distances });
          } catch (err) {
            console.warn('[measure] projections emit skipped:', err);
          }
        }
        // 4.H: emit floor plan position throttled to ~30 hz. Cleanup
        // contract: skip when no floorplan, hidden, or degenerate
        // calibration (UI keeps dot/cone hidden in those states).
        if (
          internal.floorplan !== null &&
          internal.floorplanState.visible &&
          !internal.floorplanState.degenerateCalibration &&
          now - internal.lastFloorplanEmit >= 33
        ) {
          internal.lastFloorplanEmit = now;
          try {
            const cur = currentCamera();
            const [px, py] = worldToPixel([cur.pos[0], cur.pos[2]], internal.floorplan.calibration);
            const heading = cameraHeadingDeg(cur.pos, cur.lookAt);
            emit('floorplan-position', { pxX: px, pxY: py, headingDeg: heading });
          } catch (err) {
            console.warn('[floorplan] position emit skipped:', err);
          }
        }
      });
      const unsubWarn = monitor.onWarning(() => {
        emit('fps-warning', { fps: monitor.getCurrentFps() });
      });
      internal.loadedTeardown.push(unsubFrame, unsubWarn);
    }

    // 4.K: adapter XR session lifecycle events. SESSION_STARTED is
    // gated by xrSessionSeq (a stale start triggered by an old click
    // would arrive here after teardown — drop it). SESSION_ENDED is
    // unconditional because the end signal can come from outside our
    // control (HMD power-off, headset removed) and must always settle
    // the SM.
    if (canEnterXR(deps.adapter) && canExitXR(deps.adapter)) {
      const onXRStarted = (ev: Event): void => {
        const detail = (ev as CustomEvent<{ mode: 'vr' | 'ar' }>).detail;
        dispatchXREvent({ type: 'SESSION_STARTED', mode: detail.mode });
      };
      const onXREnded = (): void => {
        dispatchXREvent({ type: 'SESSION_ENDED' });
      };
      deps.adapter.events.addEventListener('xr-session-started', onXRStarted);
      deps.adapter.events.addEventListener('xr-session-ended', onXREnded);
      internal.xrAdapterUnsubStarted = () =>
        deps.adapter.events.removeEventListener('xr-session-started', onXRStarted);
      internal.xrAdapterUnsubEnded = () =>
        deps.adapter.events.removeEventListener('xr-session-ended', onXREnded);
      internal.loadedTeardown.push(internal.xrAdapterUnsubStarted, internal.xrAdapterUnsubEnded);
    }

    // 4.E + 4.L + 4.F: keyboard shortcuts (only active in loaded state).
    // Source of truth for keys is src/lib/shortcuts.ts SHORTCUTS.
    const unbindKeys = bindShortcuts(window, {
      [SHORTCUTS.fullscreen]: () => {
        void api.toggleFullscreen();
      },
      // Step 7 #3 fix: screenshot shortcut removed — collided with
      // WASD walk movement (S = backward). Now only via toolbar button.
      [SHORTCUTS.reset]: () => api.resetView(),
      [SHORTCUTS.perf]: () => api.togglePerfHud(),
      [SHORTCUTS.share]: () => {
        void api.shareUrl();
      },
      [SHORTCUTS.tour]: () => {
        // 4.F: 't' toggles tour. From idle → start; from any active state →
        // exit. Pause is reachable via the pill button only (keyboard
        // semantics stay simple).
        if (internal.tourState.kind === 'idle') api.startTour();
        else api.exitTour();
      },
      [SHORTCUTS.hotspots]: () => api.toggleHotspots(),
      [SHORTCUTS.floorplan]: () => api.toggleFloorplan(),
      // Step 7 #3 fix: measure shortcut removed — D collided with WASD
      // walk right-strafe. Now only via toolbar button.
      [SHORTCUTS.walk]: () => {
        // 4.M: walk shortcut on touch devices is a no-op modulo a
        // toast notice (the toolbar walk button has the same gating).
        if (deps.deviceMatcher.snapshot.pointerCoarse) {
          emit('walk-error', { reason: 'touch', embed: deps.embed });
          return;
        }
        api.toggleWalk();
      },
      [SHORTCUTS.xr]: () => api.toggleXR(),
      Backspace: () => {
        // 4.J: explicit return false when not consuming so browser
        // defaults (back navigation, history input focus) survive when
        // measure mode is inactive or there are no points to remove.
        if (!internal.measureState.active || internal.measureState.points.length === 0) {
          return false;
        }
        api.removeLastMeasurePoint();
        return true;
      },
      Escape: () => {
        // 4.F + 4.G + 4.J + 4.I1 + 4.K priority:
        //   tour > hotspot popup > measure > walk > xr modal > fullscreen.
        // Tour wins because exiting it frees the camera the user expects
        // to control; xr modal lives below walk so the user can dismiss
        // FPV first if both are stacked. xr in-session does NOT consume
        // ESC (browser/headset owns the exit ergonomics).
        if (internal.tourState.kind !== 'idle') {
          api.exitTour();
          return true;
        }
        if (internal.hotspotsState.openIndex !== -1) {
          api.closeHotspot();
          return true;
        }
        if (internal.measureState.active) {
          api.exitMeasure();
          return true;
        }
        if (internal.walkState.active) {
          api.exitWalk();
          return true;
        }
        // 4.K Codex iter 1 plan MAJOR #3 fix: ESC during status='starting'
        // is consumed (return true) but does NOT dispatch CLOSE_MODAL —
        // the reducer would no-op anyway, but consuming prevents
        // accidental fall-through to fullscreen exit while the session
        // is still being granted.
        if (internal.xrState.modalOpen) {
          if (internal.xrState.status === 'starting') return true;
          api.closeXRModal();
          return true;
        }
        if (isFullscreen()) {
          void exitFullscreen();
          return true;
        }
        return false;
      },
    });
    internal.loadedTeardown.push(unbindKeys);

    // Step 7 — Bug J fix: keep WebGL backbuffer + camera aspect in sync
    // with the canvas CSS size. Without this, entering/exiting fullscreen
    // (or window resize) leaves renderer.setSize + camera.aspect at the
    // values computed in adapter.init() — Three.js stretches the stale
    // backbuffer over the new viewport, distorting the scene horizontally.
    // ResizeObserver covers fullscreenchange + resize + DPR change in a
    // single subscription. setSize(_, _, false) preserves CSS sizing
    // (the canvas keeps width:100vw;height:100vh from main.ts).
    const renderer = deps.adapter.getRenderer();
    const camera = deps.adapter.getCamera();
    const ro = new ResizeObserver(() => {
      const w = deps.canvas.clientWidth;
      const h = deps.canvas.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      const nextAspect = w / h;
      if (camera.aspect !== nextAspect) {
        camera.aspect = nextAspect;
        camera.updateProjectionMatrix();
      }
    });
    ro.observe(deps.canvas);
    internal.loadedTeardown.push(() => ro.disconnect());
  };

  const teardownLoadedFeatures = (): void => {
    // 4.F: stop any in-flight tour animation and reset state before unhooking
    // the per-frame subscription (which would otherwise drop ticker events).
    internal.tourAbort?.abort();
    internal.tourAbort = null;
    internal.tourState = { kind: 'idle' };
    internal.sortedPresets = [];
    internal.lastSnapshotAt = 0;
    // 4.G: same teardown discipline for hotspots — abort fly-to before the
    // ticker is removed so finally() callbacks don't reactivate controls
    // mid-cleanup.
    internal.hotspotAbort?.abort();
    internal.hotspotAbort = null;
    internal.hotspotsState = { openIndex: -1, visible: false };
    internal.sortedHotspots = [];
    // 4.H: floor plan minimap — no fly to abort, just clear cached state.
    internal.floorplanState = { visible: false, imageError: false, degenerateCalibration: false };
    internal.floorplan = null;
    internal.lastFloorplanEmit = 0;
    // 4.J: detach measure click handler + reset state. removeMeasureClickHandler
    // is a no-op if not currently installed.
    removeMeasureClickHandler();
    internal.measureState = { active: false, points: [], units: 'm' };
    internal.lastMeasureProjEmit = 0;
    internal.measurePointSeq = 0;
    // 4.I1: walk mode teardown — exit pointer-lock, detach listeners,
    // unsubscribe RAF ticker, reset cursor + state.
    if (internal.walkState.active) {
      if (
        typeof document.exitPointerLock === 'function' &&
        document.pointerLockElement === deps.canvas
      ) {
        document.exitPointerLock();
      }
    }
    if (internal.walkUnsubFrame !== null) {
      internal.walkUnsubFrame();
      internal.walkUnsubFrame = null;
    }
    removeWalkInputHandlers();
    deps.canvas.style.cursor = '';
    internal.walkState = { active: false, eyeHeight: EYE_HEIGHT, floorHeight: FLOOR_HEIGHT };
    internal.walkYaw = 0;
    internal.walkPitch = 0;
    internal.walkVY = 0;
    internal.walkLastTickMs = 0;
    internal.walkJumpHeld = false;
    // 4.K Codex iter 3 plan MAJOR fix: when 'starting' or 'in-session'
    // we must call api.exitXR() to either cancel the pending start
    // (cancel guard via xrSessionSeq + adapter.xrStartSeq) or end the
    // live session. Fire-and-forget — teardown can't await. The
    // wasStarting branch in api.exitXR ensures the SM transitions
    // back to idle even though no 'xr-session-ended' adapter event
    // will fire (session never reached setSession).
    if (internal.xrState.status === 'starting' || internal.xrState.status === 'in-session') {
      void api.exitXR();
    }
    // Bump the seq so any in-flight detection or enterXR catch is
    // invalidated (the .catch checks against the captured seq).
    internal.xrSessionSeq += 1;
    internal.xrState = INITIAL_XR_STATE;
    internal.xrModalOpenerEl = null;
    internal.xrAdapterUnsubStarted = null;
    internal.xrAdapterUnsubEnded = null;
    while (internal.loadedTeardown.length > 0) {
      const fn = internal.loadedTeardown.pop();
      if (fn !== undefined) {
        try {
          fn();
        } catch (err) {
          console.warn('[orchestrator] loaded teardown threw:', err);
        }
      }
    }
  };

  const api: Orchestrator = {
    events,
    async start(): Promise<void> {
      // ...assigned below
      throw new Error('start not yet bound');
    },
    async dispose(): Promise<void> {
      throw new Error('dispose not yet bound');
    },
    async takeScreenshot(): Promise<void> {
      if (!canCaptureFrame(deps.adapter)) {
        console.warn('[orchestrator] screenshot capability not available');
        return;
      }
      try {
        const frame = await deps.adapter.captureFrame();
        const filename = defaultScreenshotFilename(internal.lastFilename ?? 'oniri-viewer');
        downloadBlob(filename, frame.blob);
        // Revoke after a short delay so the download anchor has time to read it.
        setTimeout(() => frame.revoke(), 1000);
      } catch (err) {
        console.warn('[orchestrator] screenshot failed:', err);
      }
    },
    resetView(): void {
      // 4.F: leaving a running tour first keeps the SM in sync with what the
      // user sees (camera snaps to initialCamera). Otherwise the tour would
      // keep ticking against a stale camera baseline.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'EXIT' });
      }
      const fit = internal.initialCamera;
      if (fit === null) {
        console.warn('[orchestrator] no initial camera saved (resetView no-op)');
        return;
      }
      try {
        deps.adapter.setCamera(fit.pos, fit.lookAt, fit.fov);
        if (internal.controls !== null) {
          internal.controls.target.set(fit.lookAt[0], fit.lookAt[1], fit.lookAt[2]);
          internal.controls.update();
        }
      } catch (err) {
        console.warn('[orchestrator] resetView failed:', err);
      }
    },
    async toggleFullscreen(): Promise<void> {
      try {
        if (isFullscreen()) {
          await exitFullscreen();
        } else {
          // Fullscreen target must contain BOTH layers (canvas + ui) so the
          // toolbar/perf HUD/hints stay visible. Walk up to the nearest
          // ancestor that includes the UI root sibling, falling back to
          // documentElement when the layout is unexpected.
          const canvasLayer = deps.canvas.parentElement;
          const target = canvasLayer?.parentElement ?? canvasLayer ?? document.documentElement;
          await requestFullscreen(target);
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : 'unknown';
        emit('fullscreen-error', { message });
      }
    },
    togglePerfHud(): void {
      internal.perfHudVisible = !internal.perfHudVisible;
      emit('perf-toggle', { visible: internal.perfHudVisible });
    },
    async applyPreset(presetId: string): Promise<void> {
      const idx = internal.sortedPresets.findIndex((p) => p.id === presetId);
      if (idx < 0) {
        console.warn('[orchestrator] applyPreset: unknown preset id', presetId);
        return;
      }
      // 4.G: a preset shifts the camera to a destination unrelated to the
      // hotspot popup; close it so the popup doesn't anchor to a stale view.
      if (internal.hotspotsState.openIndex !== -1) {
        dispatchHotspotEvent({ type: 'CLOSE' });
      }
      // 4.F: tour-aware. While a tour is running, hand off to the SM via
      // GOTO so the dispatch unifies effect scheduling and UI snapshots.
      if (internal.tourState.kind !== 'idle') {
        dispatchTourEvent({ type: 'GOTO', index: idx, now: performance.now() });
        return;
      }
      // Manual idle path: simple flyTo + emit 'preset-applied'.
      // Race-safe: localCtrl is the ownership token. The finally block only
      // re-enables controls if WE still own internal.tourAbort AND tourState
      // is still idle — otherwise a newer tour transition owns them and the
      // tour scheduler will re-enable on its own dwelling/idle entry.
      const cur = currentCamera();
      if (internal.controls !== null) internal.controls.enabled = false;
      internal.tourAbort?.abort();
      const localCtrl = new AbortController();
      internal.tourAbort = localCtrl;
      try {
        const target = internal.sortedPresets[idx] as Preset;
        // 4.M: prefers-reduced-motion → snap immediate also for the
        // manual applyPreset path (Codex iter 2 plan MINOR #2 fix —
        // the gating was previously only on tour + hotspot fly).
        const presetDuration = deps.deviceMatcher.snapshot.reducedMotion ? 0 : TRANSITION_MS;
        await flyToCamera(deps.adapter, internal.controls, cur, presetCamera(target), {
          durationMs: presetDuration,
          signal: localCtrl.signal,
        });
        if (internal.tourAbort === localCtrl) {
          emit('preset-applied', { presetId: target.id });
        }
      } catch (err) {
        if (!isAbortError(err)) console.warn('[orchestrator] applyPreset failed:', err);
      } finally {
        if (
          internal.tourAbort === localCtrl &&
          internal.tourState.kind === 'idle' &&
          internal.controls !== null
        ) {
          internal.controls.enabled = true;
        }
      }
    },
    startTour(): void {
      dispatchTourEvent({ type: 'START', now: performance.now() });
    },
    pauseTour(): void {
      dispatchTourEvent({ type: 'PAUSE', now: performance.now() });
    },
    resumeTour(): void {
      dispatchTourEvent({ type: 'RESUME', now: performance.now() });
    },
    exitTour(): void {
      dispatchTourEvent({ type: 'EXIT' });
    },
    nextTourStep(): void {
      const cur = currentTourIndex();
      if (cur < 0 || cur + 1 >= internal.sortedPresets.length) return;
      dispatchTourEvent({ type: 'GOTO', index: cur + 1, now: performance.now() });
    },
    prevTourStep(): void {
      const cur = currentTourIndex();
      if (cur <= 0) return;
      dispatchTourEvent({ type: 'GOTO', index: cur - 1, now: performance.now() });
    },
    getSortedPresets(): readonly Preset[] {
      return internal.sortedPresets;
    },
    getTourSnapshot(): TourSnapshot {
      return tourSnapshot(internal.tourState, internal.sortedPresets, performance.now());
    },
    openHotspot(id: string): void {
      const idx = internal.sortedHotspots.findIndex((h) => h.id === id);
      if (idx < 0) {
        console.warn('[orchestrator] openHotspot: unknown hotspot id', id);
        return;
      }
      dispatchHotspotEvent({ type: 'OPEN', index: idx });
    },
    closeHotspot(): void {
      dispatchHotspotEvent({ type: 'CLOSE' });
    },
    nextHotspot(): void {
      dispatchHotspotEvent({ type: 'NEXT' });
    },
    prevHotspot(): void {
      dispatchHotspotEvent({ type: 'PREV' });
    },
    toggleHotspots(): void {
      dispatchHotspotEvent({ type: 'TOGGLE_VISIBILITY' });
    },
    getSortedHotspots(): readonly Hotspot[] {
      return internal.sortedHotspots;
    },
    getHotspotsSnapshot(): HotspotsSnapshot {
      return hotspotsSnapshot(internal.hotspotsState, internal.sortedHotspots);
    },
    toggleFloorplan(): void {
      dispatchFloorplanEvent({ type: 'TOGGLE_VISIBILITY' });
    },
    closeFloorplan(): void {
      dispatchFloorplanEvent({ type: 'CLOSE' });
    },
    reportFloorplanImageError(): void {
      dispatchFloorplanEvent({ type: 'IMAGE_ERROR' });
    },
    teleportOnFloorplan(worldXZ: readonly [number, number]): void {
      teleportOnFloorplan(worldXZ);
    },
    teleportOnFloorplanPixel(pixelXY: readonly [number, number]): void {
      if (internal.floorplan === null) return;
      const worldXZ = pixelToWorld(pixelXY, internal.floorplan.calibration);
      teleportOnFloorplan(worldXZ);
    },
    toggleMeasure(): void {
      // Step 7 mutex: walk and measure use overlapping click semantics
      // (walk: WASD movement + look; measure: click two points). Active
      // simultaneously is broken UX. If we're about to ENTER measure
      // mode, exit walk first.
      if (!internal.measureState.active && internal.walkState.active) {
        dispatchWalkEvent({ type: 'EXIT' });
      }
      dispatchMeasurementEvent({ type: 'TOGGLE_ACTIVE' });
    },
    exitMeasure(): void {
      dispatchMeasurementEvent({ type: 'EXIT' });
    },
    addMeasurePoint(screenX: number, screenY: number): void {
      if (!internal.measureState.active) return;
      const adapter = deps.adapter;
      if (!canRaycast(adapter)) {
        console.warn('[measure] raycast capability unavailable — click ignored');
        return;
      }
      // Step 7 #5 fix: snap to existing measure point when click lands
      // within SNAP_PX of any already-placed point's screen projection
      // (Trimble / Forge / Polycam pattern). Lets the user close chains
      // and re-anchor segments without sub-pixel precision.
      const SNAP_PX = 12;
      try {
        const camera = deps.adapter.getCamera();
        const viewport = {
          width: deps.canvas.clientWidth,
          height: deps.canvas.clientHeight,
        };
        for (const existing of internal.measureState.points) {
          const proj = projectWorldToScreen(existing.world, camera, viewport);
          if (!proj.visible) continue;
          const dx = proj.x - screenX;
          const dy = proj.y - screenY;
          if (Math.hypot(dx, dy) <= SNAP_PX) {
            // Reuse the existing world coords (visually identical, makes
            // chains terminate exactly on the start point).
            internal.measurePointSeq += 1;
            dispatchMeasurementEvent({
              type: 'ADD_POINT',
              id: `m_${internal.measurePointSeq}`,
              world: [...existing.world] as [number, number, number],
            });
            return;
          }
        }
      } catch (err) {
        // If projection fails (camera unavailable etc.), fall through to
        // a regular raycast — snap is best-effort.
        console.warn('[measure] snap projection skipped:', err);
      }

      // canRaycast narrowed `adapter` to `WithRaycast`, so .raycast is
      // guaranteed defined here.
      let hit: import('three').Vector3 | null;
      try {
        hit = adapter.raycast(screenX, screenY);
      } catch (err) {
        console.warn('[measure] raycast threw:', err);
        return;
      }
      if (hit === null) return;
      internal.measurePointSeq += 1;
      const id = `m_${internal.measurePointSeq}`;
      dispatchMeasurementEvent({
        type: 'ADD_POINT',
        id,
        world: [hit.x, hit.y, hit.z],
      });
    },
    removeLastMeasurePoint(): void {
      dispatchMeasurementEvent({ type: 'REMOVE_LAST' });
    },
    resetMeasurements(): void {
      dispatchMeasurementEvent({ type: 'RESET_ALL' });
    },
    setMeasurementUnits(units: MeasurementUnits): void {
      dispatchMeasurementEvent({ type: 'SET_UNITS', units });
    },
    getMeasurementsSnapshot(): MeasurementsSnapshot {
      return measurementsSnapshot(internal.measureState);
    },
    getRaycastAvailable(): boolean {
      return canRaycast(deps.adapter);
    },
    toggleWalk(): void {
      // Step 7 mutex: see toggleMeasure for rationale. If we're about to
      // ENTER walk, exit measure first.
      if (!internal.walkState.active && internal.measureState.active) {
        dispatchMeasurementEvent({ type: 'EXIT' });
      }
      dispatchWalkEvent({ type: 'TOGGLE_ACTIVE' });
    },
    exitWalk(): void {
      dispatchWalkEvent({ type: 'EXIT' });
    },
    getWalkSnapshot(): WalkSnapshot {
      return walkSnapshot(internal.walkState);
    },
    toggleXR(): void {
      const s = internal.xrState;
      // 'starting' is intentionally a no-op (orphan session prevention).
      if (s.status === 'starting') return;
      if (s.status === 'in-session') {
        void api.exitXR();
        return;
      }
      if (s.modalOpen) {
        api.closeXRModal();
        return;
      }
      api.openXRModal();
    },
    openXRModal(): void {
      dispatchXREvent({ type: 'OPEN_MODAL' });
    },
    closeXRModal(): void {
      dispatchXREvent({ type: 'CLOSE_MODAL' });
    },
    enterXR(mode: XRMode): void {
      dispatchXREvent({ type: 'SESSION_REQUEST', mode });
    },
    async exitXR(): Promise<void> {
      if (!canExitXR(deps.adapter)) return;
      // Codex iter 4 plan MAJOR #1 fix: bump xrSessionSeq when status
      // is 'starting' so the still-pending adapter.enterXR.catch
      // (captured an older seq) is invalidated and won't dispatch
      // ERROR after we tear down.
      const wasStarting = internal.xrState.status === 'starting';
      if (wasStarting) internal.xrSessionSeq += 1;
      try {
        await deps.adapter.exitXR();
      } catch (err) {
        console.warn('[orchestrator] exitXR threw:', err);
      }
      // For the 'starting' path the adapter cancelled the pending
      // session WITHOUT firing the 'xr-session-ended' event (the
      // session never reached setSession). Dispatch SESSION_ENDED
      // locally so the SM transitions starting → idle.
      if (wasStarting && !internal.disposed) {
        dispatchXREvent({ type: 'SESSION_ENDED' });
      }
    },
    getXRSnapshot(): XRSnapshot {
      return xrSnapshot(internal.xrState);
    },
    getDeviceCapabilities(): DeviceCapabilities {
      return deps.deviceMatcher.snapshot;
    },
    notifyUnavailableFeature(reason: 'walk-touch'): void {
      // Currently the only reason; switch shape kept for future
      // expansion (e.g. xr-touch, measure-touch). Toolbar callback
      // forwards here; we re-emit on the same 'walk-error' channel
      // as the pointer-lock failure paths so the renderer's toast
      // wiring stays single-purpose.
      if (reason === 'walk-touch') {
        emit('walk-error', { reason: 'touch', embed: deps.embed });
      }
    },
    getCameraState() {
      // 5.C iter 4: refuse to return a fallback lookAt [0,0,0] when
      // controls are missing. Without controls.target the orchestrator
      // cannot know what the user is aiming at, and authoring would
      // silently save hotspot/preset with a wrong target. The Studio
      // bridge gates this call by waiting for `viewer-ready`, which
      // is only emitted when controls attached successfully.
      if (internal.controls === null) {
        throw new Error('getCameraState: orbit controls not attached');
      }
      const cur = currentCamera();
      const fov = Math.min(170, Math.max(10, Number.isFinite(cur.fov) ? cur.fov : 50));
      const finite3 = (v: readonly number[]): readonly [number, number, number] => {
        const x = Number.isFinite(v[0]) ? (v[0] as number) : 0;
        const y = Number.isFinite(v[1]) ? (v[1] as number) : 0;
        const z = Number.isFinite(v[2]) ? (v[2] as number) : 0;
        return [x, y, z] as const;
      };
      return {
        cam_pos: finite3(cur.pos),
        cam_lookat: finite3(cur.lookAt),
        cam_fov: fov,
      };
    },
    raycastFromScreen(screenX: number, screenY: number) {
      if (!canRaycast(deps.adapter)) return null;
      let hit: import('three').Vector3 | null;
      try {
        hit = deps.adapter.raycast(screenX, screenY);
      } catch {
        return null;
      }
      if (hit === null) return null;
      const x = Number.isFinite(hit.x) ? hit.x : null;
      const y = Number.isFinite(hit.y) ? hit.y : null;
      const z = Number.isFinite(hit.z) ? hit.z : null;
      if (x === null || y === null || z === null) return null;
      return [x, y, z] as const;
    },
    raycastCenter() {
      if (!canRaycast(deps.adapter)) return null;
      const renderer = deps.adapter.getRenderer();
      const w = renderer.domElement.clientWidth;
      const h = renderer.domElement.clientHeight;
      if (w <= 0 || h <= 0) return null;
      const hit = deps.adapter.raycast(w / 2, h / 2);
      if (hit === null) return null;
      const x = Number.isFinite(hit.x) ? hit.x : null;
      const y = Number.isFinite(hit.y) ? hit.y : null;
      const z = Number.isFinite(hit.z) ? hit.z : null;
      if (x === null || y === null || z === null) return null;
      return [x, y, z] as const;
    },
    replaceHotspots(hotspots) {
      // Capture the active hotspot ID BEFORE swapping the sorted list,
      // so we can detect removal even when another entry takes the
      // same index after sort.
      const cur = internal.hotspotsState;
      const activeId =
        cur.openIndex >= 0 ? (internal.sortedHotspots[cur.openIndex]?.id ?? null) : null;
      const oldEmpty = internal.sortedHotspots.length === 0;
      const sorted = sortHotspots(hotspots);
      internal.sortedHotspots = sorted;
      if (activeId !== null && !sorted.some((h) => h.id === activeId)) {
        dispatchHotspotEvent({ type: 'CLOSE' });
      }
      emit('hotspot-state', hotspotsSnapshot(internal.hotspotsState, internal.sortedHotspots));
      // 5.C iter 6 fix: cardinalità crossed 0↔N → ask renderer to remount
      // the Loaded component (mountHotspotDots is no-op when wired with
      // an empty initial list).
      const newEmpty = sorted.length === 0;
      if (oldEmpty !== newEmpty) emit('metadata-replaced', { kind: 'hotspots' });
    },
    replacePresets(presets) {
      const ts = internal.tourState;
      const activeIndex =
        ts.kind === 'transitioning'
          ? ts.to
          : ts.kind === 'dwelling' || ts.kind === 'paused'
            ? ts.index
            : -1;
      const activeId = activeIndex >= 0 ? (internal.sortedPresets[activeIndex]?.id ?? null) : null;
      const oldEmpty = internal.sortedPresets.length === 0;
      const sorted = sortPresets(presets);
      internal.sortedPresets = sorted;
      // Exit the tour if the active preset disappeared (compare by id,
      // not just by length — a different preset might now occupy the
      // same index after sort).
      if (ts.kind !== 'idle' && activeId !== null && !sorted.some((p) => p.id === activeId)) {
        dispatchTourEvent({ type: 'EXIT' });
      }
      emit(
        'tour-state',
        tourSnapshot(internal.tourState, internal.sortedPresets, performance.now()),
      );
      const newEmpty = sorted.length === 0;
      if (oldEmpty !== newEmpty) emit('metadata-replaced', { kind: 'presets' });
    },
    getFloorplan(): FloorPlan | null {
      return internal.floorplan;
    },
    getFloorplanSnapshot(): FloorplanSnapshot {
      return floorplanSnapshot(internal.floorplanState);
    },
    replaceFloorplan(fp: FloorPlan | null) {
      const had = internal.floorplan !== null;
      const has = fp !== null;
      internal.floorplan = fp;
      internal.floorplanState = {
        visible: has,
        imageError: false,
        degenerateCalibration: has && isCalibrationDegenerate(fp.calibration),
      };
      // Reset the throttle clock so the renderer's first remount sees a
      // fresh emit on the next ticker frame (the per-frame
      // 'floorplan-position' loop guards on internal.floorplan === null).
      internal.lastFloorplanEmit = 0;
      emit('floorplan-state', floorplanSnapshot(internal.floorplanState));
      if (had !== has || has) {
        // 5.D iter 2 add: emit on every replace (not only 0<->1) so the
        // renderer cleans up the minimap when the image_url changes
        // (replace existing). Renderer resets currentFloorplanPosition
        // before remount.
        emit('floorplan-replaced', { hadFloorplan: had, hasFloorplan: has });
      }
    },
    async shareUrl(): Promise<void> {
      let cam: import('../viewer/adapter.js').CameraFit;
      try {
        const camera = deps.adapter.getCamera();
        const target = internal.controls?.target;
        cam = {
          pos: [camera.position.x, camera.position.y, camera.position.z],
          lookAt: target ? [target.x, target.y, target.z] : [0, 0, 0],
          fov: camera.fov,
        };
      } catch (err) {
        emit('share-result', {
          success: false,
          message: err instanceof Error ? err.message : 'camera non disponibile',
        });
        return;
      }
      const encoded = encodeCameraState(cam);
      const shareHref = buildShareUrl(window.location.href, encoded);
      try {
        await copyText(shareHref);
        // replaceState success-only: refresh continues from this view; does
        // not pollute history with intermediate share clicks.
        history.replaceState(null, '', shareHref);
        emit('share-result', { success: true });
      } catch (err) {
        emit('share-result', {
          success: false,
          message: err instanceof Error ? err.message : 'clipboard non disponibile',
        });
      }
    },
  };

  const onStateChange = (state: AppState): void => {
    if (internal.disposed) return;
    switch (state.kind) {
      case 'fetching-meta':
        void fetchMeta(state.route);
        break;
      case 'loading-scene':
        // Only kick off scene load on entry to this state. State updates from
        // MODEL_PROGRESS / METADATA_PARTIAL keep state.kind === 'loading-scene'
        // but the loadScene promise is already running; do nothing.
        break;
      default:
        break;
    }
  };

  let lastKindStarted: AppState['kind'] | null = null;
  const wrappedListener = (state: AppState): void => {
    if (state.kind !== lastKindStarted) {
      lastKindStarted = state.kind;
      onStateChange(state);
      if (state.kind === 'loading-scene') {
        void loadScene(state.route, state.meta);
      }
    }
  };

  api.start = async (): Promise<void> => {
    if (internal.disposed) return;
    internal.unsubscribe = deps.store.subscribe(wrappedListener);

    // 4.M: subscribe to device matcher and re-broadcast as
    // 'device-state' event. The renderer listens via the same
    // EventTarget channel as walk-state / xr-state etc., so the UI
    // pattern stays consistent. We also emit the initial snapshot
    // immediately so the renderer's first read is consistent with
    // the matcher's current state.
    internal.unsubDeviceMatcher = deps.deviceMatcher.subscribe((next) => {
      if (internal.disposed) return;
      emit('device-state', next);
    });
    emit('device-state', deps.deviceMatcher.snapshot);

    const initOpts = buildInitOptions(deps.canvas, deps.embed);
    try {
      await deps.adapter.init(initOpts);
    } catch (err) {
      console.error('[orchestrator] adapter init failed:', err);
      deps.store.dispatch({
        type: 'ERROR_TRANSIENT',
        route: null,
        reason: 'adapter-init-failed',
        canRetry: false,
        attempt: 1,
      });
      // 5.C: also surface to embedded bridge so the editor SM can reach
      // model-error instead of staying in loading-model forever.
      const message = err instanceof Error ? err.message : 'adapter-init-failed';
      emit('viewer-failure', { message });
      return;
    }

    deps.store.dispatch({ type: 'PARSE_URL', href: deps.href });
    const parsed = parseShareUrl(deps.href);
    deps.store.dispatch({ type: 'URL_PARSED', result: parsed });
  };

  api.dispose = async (): Promise<void> => {
    if (internal.disposed) return;
    internal.disposed = true;
    internal.abortController.abort();
    teardownLoadedFeatures();
    if (internal.unsubscribe !== null) {
      internal.unsubscribe();
      internal.unsubscribe = null;
    }
    // 4.M: stop re-broadcasting matcher changes. The matcher itself
    // is owned by bootstrap (main.ts/embed.ts) which calls dispose()
    // AFTER this — we just unsubscribe so no more 'device-state'
    // events fire post-dispose.
    if (internal.unsubDeviceMatcher !== null) {
      internal.unsubDeviceMatcher();
      internal.unsubDeviceMatcher = null;
    }
    if (canSetBeforeRender(deps.adapter)) {
      deps.adapter.setBeforeRender(null);
    }
    if (internal.controls !== null) {
      internal.controls.dispose();
      internal.controls = null;
    }
    revokeBlob();
    if (canDispose(deps.adapter)) {
      await deps.adapter.dispose();
    }
  };

  return api;
}

function ensureMetadataPresent(store: AppStore, metadata: SceneMetadata): void {
  // The orchestrator may have dispatched METADATA_PARTIAL only for successful
  // endpoints; failures use defaults from fetchMetadataTolerant. We dispatch
  // any missing keys here so the SCENE_LOADED transition (which requires
  // complete metadata) succeeds.
  const current = store.getState();
  if (current.kind !== 'loading-scene') return;
  const partial = current.metadata;
  if (!('hotspots' in partial)) {
    store.dispatch({ type: 'METADATA_PARTIAL', key: 'hotspots', value: metadata.hotspots });
  }
  if (!('presets' in partial)) {
    store.dispatch({ type: 'METADATA_PARTIAL', key: 'presets', value: metadata.presets });
  }
  if (!('floorplan' in partial)) {
    store.dispatch({ type: 'METADATA_PARTIAL', key: 'floorplan', value: metadata.floorplan });
  }
}
